import React, { useState, useEffect } from 'react';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { Button, InputNumber } from 'antd';
import { EDIT_CAMPAIGN_SETTINGS } from 'api/campaignV2/mutations';
import {
  GetCampaignsV2,
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2Variables,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  EditCampaignSettings,
  EditCampaignSettingsVariables,
} from 'api/campaignV2/types/EditCampaignSettings';
import { successNotification, errorNotification } from 'ui/Notification';
import styles from './ChangePlatformMargin.module.scss';

type ChangePlatformMarginProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  refetch: (
    variables?: Partial<GetCampaignsV2Variables> | undefined
  ) => Promise<ApolloQueryResult<GetCampaignsV2>>;
  disabled?: boolean;
};

const DEFAULT_MARGIN = 20;

const ChangePlatformMargin = ({
  record,
  refetch,
  disabled = false,
}: ChangePlatformMarginProps): JSX.Element | null => {
  const currentMargin = record?.platformMarginPercent ?? DEFAULT_MARGIN;
  const [marginValue, setMarginValue] = useState<number>(currentMargin);

  useEffect(() => {
    setMarginValue(currentMargin);
  }, [currentMargin]);

  const [editCampaignSettings, { loading }] = useMutation<
    EditCampaignSettings,
    EditCampaignSettingsVariables
  >(EDIT_CAMPAIGN_SETTINGS);

  const handleMarginChange = (value: number | null) => {
    if (value !== null && value >= 0 && value <= 100) {
      setMarginValue(value);
    }
  };

  const handleUpdateMargin = async () => {
    if (record?.id && marginValue >= 0 && marginValue <= 100) {
      try {
        const { data } = await editCampaignSettings({
          variables: {
            input: {
              campaignId: record?.id,
              platformMarginPercent: marginValue,
            },
          },
        });
        if (data?.editCampaignSettings.id) {
          successNotification('Platform margin has been updated');
        }

        refetch();
      } catch (error: any) {
        console.error('Error updating platform margin', error);
        errorNotification(error?.message || 'Error updating platform margin');
      }
    } else {
      errorNotification('Invalid margin value (must be 0-100)');
    }
  };

  const hasChanged = marginValue !== currentMargin;

  return (
    <div className={styles.root}>
      <p className={styles.label}>Platform Margin</p>
      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <InputNumber
            min={0}
            max={100}
            precision={0}
            value={marginValue}
            onChange={handleMarginChange}
            style={{ width: 80 }}
            disabled={disabled}
          />
          <span className={styles.percentSign}>%</span>
        </div>

        {!disabled && (
          <Button
            type="primary"
            onClick={handleUpdateMargin}
            loading={loading}
            disabled={!hasChanged}
          >
            Update margin
          </Button>
        )}
      </div>
      <p className={styles.explanation}>
        {disabled
          ? 'Cannot be changed after campaign approval.'
          : 'Affects talent payout. Can only be changed before campaign approval.'}
      </p>
    </div>
  );
};

export default ChangePlatformMargin;
